import React, { useState, useEffect, useContext, useRef } from "react";
import { PlayButton } from "./components";
import styled, { ThemeContext } from "styled-components";
import { hexToRGB } from "../Utils";
import { AudioContext } from "standardized-audio-context";
import { BrowserInfo } from "../"; //iOS versions 13.x (expected to be fixed in 13.4) have a bug that breaks webAudio when using `createMediaElementSource`
//should be fixed by these changes https://trac.webkit.org/changeset/254550/webkit
//don't call the API on the affected devices
//have to check for the browser version since iPad reports as Desktop Mac

const safariRegex = /AppleWebKit\/605/;
const isBadSafariMobile = safariRegex.test(BrowserInfo.getUA());
const isBadSafari = isBadSafariMobile && navigator.maxTouchPoints > 0;
const AudioPlayerContainer = styled.div`
  height: 75px;
  display: flex;
  align-items: center;
  transition: padding 250ms ease;
  padding: 0 16px 0 16px;
  order: -1;
  @media only screen and ${props => props.theme.media.large} {
    order: 0;
    padding: 0 16px 0 24px;
  }
`;
const CanvasContainer = styled.div`
  overflow: hidden;
  transition: height 250ms ease;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
`;
const VisualCanvas = styled.canvas`
  display: block;
  height: 50px;
  width: 100%;
  position: relative;
`;
export const AudioPlayer = ({
  src,
  onPlay
}) => {
  const theme = useContext(ThemeContext);
  const [rawR, rawG, rawB] = hexToRGB(theme.colors.action);
  const [id] = useState(Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15));
  const [usRef] = useState({});
  const audioContext = useRef();
  const playing = useRef(false);
  const [playingState, setPlayingState] = useState(false);
  const [progress, setProgress] = useState(0);
  const [playRef, setPlayRef] = useState();
  const canvasElement = /*#__PURE__*/React.createRef();
  useEffect(() => {
    if (playRef) {
      playRef.addEventListener("click", startPlay);
      document.addEventListener("max_audio_start", handleOtherStart);
      return () => {
        playRef.removeEventListener("click", startPlay);
        document.removeEventListener("max_audio_start", handleOtherStart);
      };
    }
  }, [usRef, playRef]);

  const savePlayRef = ref => {
    setPlayRef(ref);
  };

  const handleOtherStart = e => {
    if (e.detail.source !== usRef) {
      handleStop();
    }
  };

  const handleStop = () => {
    if (playing.current) {
      audioContext.current.close();
      document.getElementById(`${id}audio`).remove();
      playing.current = false;
      setProgress(0);
      setPlayingState(false);
      onPlay(false);
    }
  };

  const handleStart = context => {
    const event = new CustomEvent("max_audio_start", {
      detail: {
        source: usRef
      }
    });
    document.dispatchEvent(event);
    audioContext.current = context;
    setProgress(0);
    playing.current = true;
  };

  const handleEnded = () => {
    setTimeout(() => {
      handleStop();
    }, 1000);
  };

  const handleTimeUpdate = ({
    target: {
      currentTime,
      duration
    }
  }) => {
    setProgress(currentTime / duration);
  };

  const startPlay = () => {
    if (playing.current) {
      return;
    }

    playing.current = true;
    var canvas = document.getElementById(`${id}canvas`);
    var ctx = canvas.getContext("2d");
    var audioContext = new AudioContext();
    handleStart(audioContext);
    var analyser = audioContext.createAnalyser();
    const container = document.getElementById(`${id}container`);
    analyser.fftSize = 256;
    let bufferLength = analyser.frequencyBinCount;
    let dataArray = new Uint8Array(bufferLength);
    audioContext.resume();
    let WIDTH = container.clientWidth;
    let HEIGHT = canvas.height;
    canvas.width = WIDTH;
    let barWidth = WIDTH / (bufferLength - 35);
    let barHeight;
    let x = 0;

    const renderFrame = () => {
      if (playing.current) {
        requestAnimationFrame(renderFrame);
      }

      x = 0;
      analyser.getByteFrequencyData(dataArray);
      ctx.clearRect(0, 0, WIDTH, HEIGHT);

      for (let i = 5; i < bufferLength - 30; i++) {
        barHeight = dataArray[i];
        const adjustedBarHeight = barHeight * (HEIGHT / 255);
        let r = rawR + barHeight / 2;
        let g = rawG - i;
        let b = rawB + i;
        ctx.fillStyle = "rgb(" + r + "," + g + "," + b + ")";
        ctx.fillRect(x, HEIGHT - adjustedBarHeight, barWidth, adjustedBarHeight);
        x += barWidth + 1.5;
      }
    };

    renderFrame();
    var audio = new Audio();
    audio.id = `${id}audio`;
    audio.loop = false;
    audio.controls = false;
    audio.crossOrigin = "anonymous";
    audio.addEventListener("error", function (e) {
      console.error(e);
    });
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.src = src;
    document.getElementById(`${id}wrapper`).append(audio);

    if (!isBadSafari) {
      audio.addEventListener("canplay", function () {
        var audioSourceNode = audioContext.createMediaElementSource(audio);
        audioSourceNode.connect(analyser);
        audioSourceNode.connect(audioContext.destination);
      });
    }

    audio.play();
  };

  const handleClick = () => {
    if (playingState) {
      handleStop();
    }

    setPlayingState(!playingState);
    onPlay(!playingState);
  };

  return /*#__PURE__*/React.createElement(AudioPlayerContainer, {
    style: {
      paddingBottom: playing.current ? "50px" : ""
    }
  }, /*#__PURE__*/React.createElement(PlayButton, {
    ref: savePlayRef,
    onClick: handleClick,
    playing: playingState,
    progress: playingState ? progress : 0
  }), /*#__PURE__*/React.createElement("div", {
    id: `${id}wrapper`
  }), /*#__PURE__*/React.createElement(CanvasContainer, {
    id: `${id}container`,
    style: {
      height: playing.current ? "50px" : "0px"
    }
  }, /*#__PURE__*/React.createElement(VisualCanvas, {
    id: `${id}canvas`,
    ref: canvasElement,
    height: 50
  })));
};